import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { VehicleCard } from "@/components/vehicle/VehicleCard";
import { FilterSidebar } from "@/components/vehicle/FilterSidebar";

const SORT_OPTIONS = [
  { v: "relevance", l: "Relevance" },
  { v: "price_asc", l: "Price: Low to High" },
  { v: "price_desc", l: "Price: High to Low" },
  { v: "km_asc", l: "KM: Low to High" },
  { v: "year_desc", l: "Year: Newest First" },
];

export default function BuyCarsPage() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const filters = useMemo(() => {
    const obj = {};
    params.forEach((v, k) => { if (v !== "" && v !== undefined) obj[k] = v; });
    return obj;
  }, [params]);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== "") q.set(k, v); });
    if (!q.has("sort")) q.set("sort", "relevance");
    api.get(`/vehicles?${q.toString()}`).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0 })).finally(() => setLoading(false));
  }, [filters]);

  const updateFilters = (next) => {
    const n = { ...next };
    Object.keys(n).forEach((k) => { if (n[k] === undefined || n[k] === "" || n[k] === false) delete n[k]; });
    setParams(n, { replace: true });
  };

  const clearAll = () => setParams({});

  const activeChips = Object.entries(filters).filter(([k]) => !["sort"].includes(k));

  return (
    <div className="cs-container py-8" data-testid="buy-cars-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-display font-extrabold text-3xl text-gray-900">Buy Used Cars in Delhi NCR</h1>
          <p className="text-sm text-gray-500 mt-1" data-testid="results-count">{loading ? "Loading…" : `${data.total} cars available`}</p>
        </div>
        <div className="flex items-center gap-2">
          <Sheet>
            <SheetTrigger asChild>
              <button className="lg:hidden inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold" data-testid="mobile-filters-btn">
                <SlidersHorizontal size={14} /> Filters
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 overflow-y-auto">
              <FilterSidebar value={filters} onChange={updateFilters} onClear={clearAll} />
            </SheetContent>
          </Sheet>
          <select
            value={filters.sort || "relevance"}
            onChange={(e) => updateFilters({ ...filters, sort: e.target.value })}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold"
            data-testid="sort-select"
          >
            {SORT_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
        </div>
      </div>

      {activeChips.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="active-filters">
          {activeChips.map(([k, v]) => (
            <button key={k} onClick={() => { const n = { ...filters }; delete n[k]; updateFilters(n); }}
              className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 text-white text-xs font-semibold px-3 py-1">
              {k}: {String(v)} <X size={12} />
            </button>
          ))}
          <button onClick={clearAll} className="text-xs font-semibold text-[#940C2C]">Clear all</button>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
        <aside className="hidden lg:block border border-gray-200 rounded-xl p-5 h-fit sticky top-28">
          <FilterSidebar value={filters} onChange={updateFilters} onClear={clearAll} />
        </aside>
        <div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-80 bg-gray-100 rounded-xl animate-pulse" />)}
            </div>
          ) : data.items.length === 0 ? (
            <div className="border border-dashed border-gray-200 rounded-xl p-16 text-center">
              <h3 className="font-display font-bold text-lg text-gray-900">No cars match your filters</h3>
              <p className="text-sm text-gray-500 mt-1">Try removing some filters or clearing all.</p>
              <button onClick={clearAll} className="btn-primary mt-5">Clear filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="vehicle-grid">
              {data.items.map((v) => <VehicleCard key={v.id} vehicle={v} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
